import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { StepperContext } from "../../../stepper/context/StepperContext";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import UploadFileField from "../../../components/common-components/UploadFileField";
import TitleBar from "../../../components/common-components/TitleBar";
import { StepSubmitter } from "../../../stepper/StepSubmitter";
import { UploadIcon } from "../../../assets/Icon/dashboredIcon";
import ApiHelperFunction from "../../../api";
import { AppDispatch } from "../../../stores/store";
import ListLoadingNew from "../../../components/common-components/ListLoadingNew";
import { setToast } from "../../../stores/toast/toastslice";
import Grid from "@mui/material/Grid2";
import { StyledGrid } from "../../incorporation/Incorporation";
import { useLocation, useNavigate, useParams } from "react-router";
import { useInputHandler } from "../../../stepper/hooks/useInouthandler";
import { useStepData } from "../../../stepper/UseStepData";
import UploadMultipleFile from "../../../components/common-components/UploadMultipleFile";
import MemberAttachmentModal from "../../../components/common-components/AddMemberComponent";
import { useFormik } from "formik";
import AddressModal from "./AddDetailModal";
import ConfirmModal from "../../../components/common-components/ConfirmModal";
import { getCountryList, getStateList } from "../../../stores/incorporation";
import { updateItinAddress } from "../../../stores/itin";

export const Step2 = () => {
  const stepperState = useContext(StepperContext);
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);
  const currentStepIndex = stepperState.currentStepIdx;
  const step1Data = stepperState.data.get(0) || {};
  const [hasUSCompany, setUsCompany] = useState(step1Data.has_us_company);
  const [businessIncPreviews, setBusinessIncPreviews] = useState<string[]>([]);
  const [businessIncDocuments, setBusinessIncDocuments] = useState<File[]>([]);
  const savedPayload = stepperState.data.get(currentStepIndex) || {};
  const [getLoading, setGetLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { data } = useStepData();
  const { payload, handleChange, setData } = useInputHandler<any>(data);
  const isEditPassport = location.pathname.includes("edit-documents");
  const isEditW7 = location.pathname.includes("edit-w7");
  const isEditMode = isEditPassport || isEditW7;

  // ✅ Success popup state
  const [successPopupOpen, setSuccessPopupOpen] = useState(false);
  // Formation docs are no longer rejected; the backend instead reports
  // whether it saw an official seal. seal_present is TRI-state:
  // true / false / null, where null means the check could not run.
  const [sealNoticeOpen, setSealNoticeOpen] = useState(false);
  // The user's answer to the seal prompt, sent to /extract-data/ as consent.
  const [sealAcknowledged, setSealAcknowledged] = useState(false);
  // The seal answer the backend last gave us, stamped with the formation files
  // it applies to. Lets a retry (after fixing an unrelated field) show the
  // prompt straight away instead of waiting on another vision call, and makes
  // the answer self-invalidating the moment the documents change.
  const [cachedSeal, setCachedSeal] = useState<{
    signature: string;
    missingSeal: boolean;
  } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Set by the seal prompt's Proceed. An effect does the resubmit so it runs
  // after sealAcknowledged has actually landed in state -- calling handleSubmit
  // straight from the handler would send the old value.
  const [resubmitAfterConsent, setResubmitAfterConsent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [itinRecordId, setItinRecordId] = useState<number | null>(null);
  const [addressLoading, setAddressLoading] = useState(false);
  // Store the next-step action to execute after popup is submitted
  const [pendingNavigation, setPendingNavigation] = useState<
    (() => void) | null
  >(null);

  const [fileUploads, setFileUploads] = useState<{
    [key: string]: File | null | string;
  }>({
    passport_image: savedPayload.passport_image || "",
    ein_document: savedPayload.ein_document || "",
  });
  const {
    incorporationDetails,
    countryList,
    industryList,
    compnayTypeList,
    stateList,
    status,
  } = useSelector((store: any) => store.incorporation);

  useEffect(() => {
    if (countryList.length === 0) {
      dispatch<any>(getCountryList({}));
    }
    if (stateList.length === 0) {
      dispatch<any>(getStateList({}));
    }
  }, [countryList.length, stateList.length, dispatch]);
  const handleAddressSubmit = async (values: any) => {
    setAddressLoading(true);
    setErrorMessage("");

    try {
      const result = await dispatch<any>(
        updateItinAddress({
          order_id: id,
          address: values.member_address,
          city: values.member_city,
          state: values.state,
          zip_code: values.member_zip_code,
          country: values.country,
          phone_number: `${values.country_code}${values.phone_number}`,
        }),
      );

      if (
        updateItinAddress.fulfilled.match(result) &&
        result.payload?.status === 1
      ) {
        dispatch<any>(
          setToast({
            message: result.payload?.message || "Address saved successfully.",
            type: "success",
            title: "Success",
          }),
        );
        setSuccessPopupOpen(false);

        const nav = pendingNavigation;
        setPendingNavigation(null);
        if (nav) nav();
      } else {
        dispatch<any>(
          setToast({
            message: result.payload?.message || "Unable to save address.",
            type: "error",
            title: "Error",
          }),
        );
      }
    } catch (error: any) {
      dispatch<any>(
        setToast({
          message:
            error?.data?.message || "Failed to save address. Please try again.",
          type: "error",
          title: "Error",
        }),
      );
    } finally {
      setAddressLoading(false);
    }
  };
  const formik = useFormik({
    initialValues: {
      member_phone_number: "",
      phone_number: "",
      country_code: "",
      member_address: "",
      member_city: "",
      member_zip_code: "",
      state: "",
      country: "",
    },
    validate: (values) => {
      const errors: any = {};
      if (!values.member_address) {
        errors.member_address = "Address is required.";
      }
      if (!values.member_city) {
        errors.member_city = "City is required.";
      }
      if (!values.state) {
        errors.state = "State is required.";
      }
      if (!values.country) {
        errors.country = "Country is required.";
      }
      if (!values.member_zip_code) {
        errors.member_zip_code = "Zip code is required.";
      } else if (values.member_zip_code.trim().length < 3) {
        errors.member_zip_code = "Zip code must be at least 3 characters long.";
      }
      if (!values.phone_number) {
        errors.phone_number = "Phone number is required.";
      }
      return errors;
    },
    onSubmit: (values) => {
      handleAddressSubmit(values);
    },
  });

  const validateForm = () => {
    if (hasUSCompany === "true" || hasUSCompany === true) {
      return !!fileUploads.passport_image;
    } else {
      return (
        !!fileUploads.passport_image &&
        businessIncDocuments.length > 0 &&
        !!fileUploads.ein_document
      );
    }
  };

  const token = localStorage.getItem("user_token");
  const { id } = useParams<{ id: string }>();
  const [initialFileUploads, setInitialFileUploads] = useState(fileUploads);

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    setGetLoading(true);
    try {
      const response = await ApiHelperFunction({
        url: `/get-itin-data/?order_id=${id}`,
        method: "GET",
        headers: {
          Authorization: token ? `${token}` : "",
        },
      });

      if (response?.data?.status === 1) {
        setGetLoading(false);
        const hasUsCompany = response?.data?.data?.has_us_company;
        console.log("has_us_company value from API:", hasUsCompany);
        setUsCompany(hasUsCompany);
        setItinRecordId(response?.data?.order?.orders_id);
        const extractedData =
          response?.data?.data?.extracted_data?.passport || {};
        setUsCompany(response?.data?.data?.has_us_company);
        const scanPassportUrl = response?.data?.data?.scan_passport || "";
        const scanBusinessIncorporationUrl =
          response?.data?.data?.business_incorporation || "";
        const scanEINUrl = response?.data?.data?.ein_document || "";

        const formattedData = {
          passport_image: scanPassportUrl,
          name: extractedData.name || "",
          surname: extractedData.surname || "",
          date_of_birth: extractedData.date_of_birth || "",
          passport_number: extractedData.passport_number || "",
          place_of_birth: extractedData.place_of_birth || "",
          nationality: extractedData.nationality || "",
          country: extractedData.country || "",
          date_of_expiry: extractedData.date_of_expiry || "",
          authority: extractedData.authority || "",
          height: extractedData.height || "",
          place_of_issue: extractedData.place_of_issue || "",
          date_of_issue: extractedData.date_of_issue || "",
          sex: extractedData.sex || "",
        };

        setFileUploads({
          passport_image: scanPassportUrl,
          ein_document: scanEINUrl,
        });
        setInitialFileUploads({
          passport_image: scanPassportUrl,
          ein_document: scanEINUrl,
        });
        const extractedAddress = response?.data?.data?.extracted_data;
        if (
          extractedAddress?.address ||
          extractedAddress?.city ||
          extractedAddress?.phone_number
        ) {
          formik.setValues({
            member_phone_number: extractedAddress?.phone_number || "",
            phone_number: extractedAddress?.phone_number || "",
            country_code: "",
            member_address: extractedAddress?.address || "",
            member_city: extractedAddress?.city || "",
            member_zip_code: extractedAddress?.zip_code || "",
            state: extractedAddress?.state || "",
            country: extractedAddress?.country || "",
          });
        }
        setBusinessIncPreviews(
          scanBusinessIncorporationUrl ? [scanBusinessIncorporationUrl] : [],
        );
        if (stepperState.setData) {
          stepperState.setData(currentStepIndex, formattedData, true);
        }
      }
    } catch (error) {
      setGetLoading(false);
    } finally {
      setGetLoading(false);
    }
  };
  const openNextStep = () => {
    if (isEditMode) {
      setPendingNavigation(() => () => navigate(`/itin-detail/${id}`));
    } else {
      setPendingNavigation(
        () => () =>
          stepperState.setActiveStep &&
          stepperState.setActiveStep(stepperState.currentStepIdx + 1, true),
      );
    }

    // ✅ if has_us_company is false → show address modal first
    // ✅ if has_us_company is true  → skip modal, go directly to next step
    if (hasUSCompany === false || hasUSCompany === "false") {
      setSuccessPopupOpen(true);
    } else {
      const nav = pendingNavigation;
      setPendingNavigation(null);
      if (nav) nav();
    }
  };

  const handleFileChange = (file: File | null, key: string) => {
    setFileUploads((prev) => ({ ...prev, [key]: file }));
    // Drop any error still shown against this upload -- it referred to the file
    // being replaced.
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (stepperState.setData) {
      stepperState.setData(
        currentStepIndex,
        { ...fileUploads, [key]: file },
        true,
      );
    }
    setErrorMessage("");
  };

  useEffect(() => {
    if (resubmitAfterConsent && sealAcknowledged) {
      setResubmitAfterConsent(false);
      // A synthetic event: handleSubmit only needs preventDefault.
      handleSubmit({ preventDefault: () => {} } as React.FormEvent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resubmitAfterConsent, sealAcknowledged]);

  /**
   * Identity of the current formation upload set. A cached seal answer is only
   * valid while this is unchanged -- swap, add or remove a document and both the
   * answer and the user's consent have to go, or we would skip the prompt for a
   * document that was never checked.
   */
  const formationSignature = businessIncDocuments
    .map((f) => `${f.name}:${f.size}:${f.lastModified}`)
    .join("|");

  const invalidateSealState = () => {
    setSealAcknowledged(false);
    setCachedSeal(null);
  };

  const handleDeleteBusinessIncFile = (index: number) => {
    setBusinessIncDocuments((prev) => prev.filter((_, i) => i !== index));
    setBusinessIncPreviews((prev) => prev.filter((_, i) => i !== index));
    setErrorMessage("");
    invalidateSealState();
  };

  // ✅ Phone number popup submit handler
  const handlePhoneSubmit = async () => {
    if (!phoneNumber.trim()) {
      setPhoneError("Phone number is required.");
      return;
    }
    const phoneRegex = /^[+]?[\d\s\-().]{7,15}$/;
    if (!phoneRegex.test(phoneNumber)) {
      setPhoneError("Please enter a valid phone number.");
      return;
    }

    setPhoneError("");
    setPhoneLoading(true);

    try {
      // Optional: submit the phone number to your API here
      // await ApiHelperFunction({ url: '/save-phone/', method: 'POST', data: { phone: phoneNumber, order_id: id }, ... });

      setSuccessPopupOpen(false);
      setPhoneNumber("");

      // Execute the pending navigation/step action
      if (pendingNavigation) {
        pendingNavigation();
        setPendingNavigation(null);
      }
    } catch (error) {
      setPhoneError("Failed to save phone number. Please try again.");
    } finally {
      setPhoneLoading(false);
    }
  };

  const handlePopupClose = () => {
    setSuccessPopupOpen(false);
    setPhoneNumber("");
    setPhoneError("");
    setPendingNavigation(null);
    formik.resetForm();
  };

  // const handleSubmit = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   setErrorMessage("");

  //   if (hasUSCompany === "false" || hasUSCompany === false) {
  //     if (!fileUploads.passport_image || fileUploads.passport_image === "") {
  //       setErrorMessage("Passport is required.");
  //       return;
  //     }
  //   } else {
  //     if (
  //       !fileUploads.passport_image ||
  //       fileUploads.passport_image === "" ||
  //       businessIncDocuments.length === 0 ||
  //       !fileUploads.ein_document ||
  //       fileUploads.ein_document === ""
  //     ) {
  //       setErrorMessage("All documents must be uploaded before proceeding.");
  //       return;
  //     }
  //   }

  //   if (JSON.stringify(fileUploads) === JSON.stringify(initialFileUploads)) {
  //     if (isEditMode) {
  //       navigate(`/itin-detail/${id}`);
  //     } else {
  //       if (stepperState.setActiveStep) {
  //         stepperState.setActiveStep(currentStepIndex + 1, true);
  //       }
  //     }
  //     return;
  //   }

  //   const formData = new FormData();
  //   if (fileUploads?.passport_image instanceof File) {
  //     formData.append("passport_image", fileUploads.passport_image);
  //   }
  //   businessIncDocuments.forEach((file) => {
  //     formData.append("business_incorporation", file);
  //   });
  //   if (fileUploads?.ein_document instanceof File) {
  //     formData.append("ein_document", fileUploads.ein_document);
  //   }
  //   if (id) {
  //     formData.append("order_id", id);
  //   }
  //   if (isEditMode) {
  //     formData.append("is_updating", "yes");
  //   }

  //   try {
  //     setLoading(true);
  //     const response = await ApiHelperFunction({
  //       url: "/extract-data/",
  //       method: "POST",
  //       data: formData,
  //       headers: {
  //         Authorization: token ? `${token}` : "",
  //       },
  //     });

  //     if (response?.data?.status === 1) {
  //       setLoading(false);
  //       const passportData = response?.data?.extracted_data?.passport || {};
  //       const formattedData = {
  //         name: passportData.name || "",
  //         surname: passportData.surname || "",
  //         date_of_birth: passportData.date_of_birth || "",
  //         passport_number: passportData.passport_number || "",
  //         place_of_birth: passportData.place_of_birth || "",
  //         nationality: passportData.nationality || "",
  //         date_of_expiry: passportData.date_of_expiry || "",
  //         authority: passportData.authority || "",
  //         height: passportData.height || "",
  //         place_of_issue: passportData.place_of_issue || "",
  //         date_of_issue: passportData.date_of_issue || "",
  //         sex: passportData.sex || "",
  //       };

  //       if (stepperState.setData) {
  //         stepperState.setData(
  //           currentStepIndex,
  //           { ...fileUploads, ...formattedData },
  //           true,
  //         );
  //       }

  //       // ✅ Store the next action and open the success popup
  //       if (isEditMode) {
  //         setPendingNavigation(() => () => navigate(`/itin-detail/${id}`));
  //       } else {
  //         setPendingNavigation(
  //           () => () =>
  //             stepperState.setActiveStep &&
  //             stepperState.setActiveStep(stepperState.currentStepIdx + 1, true),
  //         );
  //       }
  //       setSuccessPopupOpen(true); // 🎉 Open popup on success
  //     }

  //     if (response?.data?.status === 0) {
  //       setLoading(false);
  //       dispatch<any>(
  //         setToast({
  //           message: response?.data?.message,
  //           type: "error",
  //           title: "Error",
  //         }),
  //       );
  //     }
  //     setLoading(false);
  //   } catch (error: any) {
  //     dispatch<any>(
  //       setToast({
  //         message: error?.data?.message,
  //         type: "error",
  //         title: "Error",
  //       }),
  //     );
  //     setLoading(false);
  //   }
  // };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");

    if (hasUSCompany === "false" || hasUSCompany === false) {
      if (!fileUploads.passport_image || fileUploads.passport_image === "") {
        setErrorMessage("Passport is required.");
        return;
      }
    } else {
      if (
        !fileUploads.passport_image ||
        fileUploads.passport_image === "" ||
        businessIncDocuments.length === 0 ||
        !fileUploads.ein_document ||
        fileUploads.ein_document === ""
      ) {
        setErrorMessage("All documents must be uploaded before proceeding.");
        return;
      }
    }

    // ── No file changes block ─────────────────────────────────────────────
    if (JSON.stringify(fileUploads) === JSON.stringify(initialFileUploads)) {
      if (isEditMode) {
        setPendingNavigation(() => () => navigate(`/itin-detail/${id}`));
      } else {
        setPendingNavigation(
          () => () =>
            stepperState.setActiveStep &&
            stepperState.setActiveStep(currentStepIndex + 1, true),
        );
      }
      setSuccessPopupOpen(true);
      return;
    }

    // ── Seal prompt, straight from cache ──────────────────────────────────
    // If the backend already told us these exact documents carry no seal, there
    // is nothing to learn by asking again -- put the question to the user now and
    // send their answer with the one submit that follows. This is the retry case:
    // they fixed a passport or EIN error and left the formation files alone.
    if (
      cachedSeal &&
      cachedSeal.signature === formationSignature &&
      cachedSeal.missingSeal &&
      !sealAcknowledged
    ) {
      setSealNoticeOpen(true);
      return;
    }

    // ── Build form data ───────────────────────────────────────────────────
    const formData = new FormData();
    if (fileUploads?.passport_image instanceof File) {
      formData.append("passport_image", fileUploads.passport_image);
    }
    businessIncDocuments.forEach((file) => {
      formData.append("business_incorporation", file);
    });
    if (fileUploads?.ein_document instanceof File) {
      formData.append("ein_document", fileUploads.ein_document);
    }
    if (id) {
      formData.append("order_id", id);
    }
    if (isEditMode) {
      formData.append("is_updating", "yes");
    }
    if (sealAcknowledged) {
      formData.append("seal_acknowledged", "true");
    }

    // ── API call ──────────────────────────────────────────────────────────
    try {
      setLoading(true);
      const response = await ApiHelperFunction({
        url: "/extract-data/",
        method: "POST",
        data: formData,
        headers: {
          Authorization: token ? `${token}` : "",
        },
      });

      // Cache the seal answer for these exact files, whatever the outcome, so a
      // retry can prompt without another round trip.
      const formationDocs = response?.data?.formation_documents ?? [];
      if (formationDocs.length) {
        setCachedSeal({
          signature: formationSignature,
          // Strict === false: null means the check could not run, and treating
          // that as "no seal" would prompt every user during an outage.
          missingSeal:
            !formationDocs.some((d: any) => d?.seal_present === true) &&
            formationDocs.some((d: any) => d?.seal_present === false),
        });
      }

      // The backend stops ahead of extraction when nothing carries a seal and we
      // have not asked yet. Nothing was written, so cancelling is safe.
      if (response?.data?.needs_seal_confirmation) {
        setLoading(false);
        setSealNoticeOpen(true);
        return;
      }

      if (response?.data?.status === 1) {
        setLoading(false);
        setFieldErrors({});
        const passportData = response?.data?.extracted_data?.passport || {};
        const formattedData = {
          name: passportData.name || "",
          surname: passportData.surname || "",
          date_of_birth: passportData.date_of_birth || "",
          passport_number: passportData.passport_number || "",
          place_of_birth: passportData.place_of_birth || "",
          nationality: passportData.nationality || "",
          country: passportData.country || "",
          date_of_expiry: passportData.date_of_expiry || "",
          authority: passportData.authority || "",
          height: passportData.height || "",
          place_of_issue: passportData.place_of_issue || "",
          date_of_issue: passportData.date_of_issue || "",
          sex: passportData.sex || "",
        };

        if (stepperState.setData) {
          stepperState.setData(
            currentStepIndex,
            { ...fileUploads, ...formattedData },
            true,
          );
        }

        if (isEditMode) {
          setPendingNavigation(() => () => navigate(`/itin-detail/${id}`));
        } else {
          setPendingNavigation(
            () => () =>
              stepperState.setActiveStep &&
              stepperState.setActiveStep(
                stepperState.currentStepIdx + 1,
                true,
              ),
          );
        }
        // Reaching here means the backend was satisfied -- either a seal was
        // found or the user had already consented -- so extraction has run.
        setSuccessPopupOpen(true);
      }

      if (response?.data?.status === 0) {
        setLoading(false);
        // `field` says which input the message belongs to, so it can sit under
        // that upload rather than only in a toast that disappears.
        const field = response?.data?.field;
        if (field) {
          setFieldErrors({ [field]: response?.data?.message });
        }
        dispatch<any>(
          setToast({
            message: response?.data?.message,
            type: "error",
            title: "Error",
          }),
        );
      }

      setLoading(false);
    } catch (error: any) {
      // ApiHelperFunction rethrows the axios response for any non-2xx, so a 400
      // lands here rather than in the status === 0 branch above -- which is why
      // the field message has to be picked up in both places.
      const field = error?.data?.field;
      if (field) {
        setFieldErrors({ [field]: error?.data?.message });
      }
      dispatch<any>(
        setToast({
          message: error?.data?.message,
          type: "error",
          title: "Error",
        }),
      );
      setLoading(false);
    }
  };
  return getLoading ? (
    <ListLoadingNew />
  ) : (
    <Box sx={{ height: "calc(100vh - 120px)" }}>
      <TitleBar title="Apply for ITIN" description="" />

      <form onSubmit={handleSubmit}>
        {hasUSCompany === "false" || hasUSCompany === false ? (
          <StyledGrid container>
            <Grid size={{ xs: 12, lg: 6 }}>
              <UploadFileField
                title="Passport"
                icon={<UploadIcon />}
                accept="image/jpeg,image/jpg,image/png,.jpeg,.jpg,.png,.pdf,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(file) => handleFileChange(file, "passport_image")}
                fileUrl={
                  typeof fileUploads.passport_image === "string"
                    ? fileUploads.passport_image
                    : undefined
                }
                showInstruction
              />
              {fieldErrors.passport_image && (
                <Box sx={{ color: "red", mt: 1, fontSize: "14px" }}>
                  {fieldErrors.passport_image}
                </Box>
              )}
            </Grid>
          </StyledGrid>
        ) : (
          <StyledGrid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <UploadMultipleFile
                accept="application/pdf,image/jpeg,image/jpg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                title="Business Incorporation"
                icon={<UploadIcon />}
                documents={businessIncDocuments}
                filePreviews={businessIncPreviews}
                setFilePreviews={setBusinessIncPreviews}
                setDocuments={setBusinessIncDocuments}
                onDelete={handleDeleteBusinessIncFile}
                showMoreButton={true}
                showBackground={true}
                showBottomInfo
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <UploadFileField
                accept="application/pdf, image/jpeg,image/jpg, image/png, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                title="EIN Document"
                icon={<UploadIcon />}
                onChange={(file) => handleFileChange(file, "ein_document")}
                fileUrl={
                  typeof fileUploads.ein_document === "string"
                    ? fileUploads.ein_document
                    : undefined
                }
              />
              {fieldErrors.ein_document && (
                <Box sx={{ color: "red", mt: 1, fontSize: "14px" }}>
                  {fieldErrors.ein_document}
                </Box>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <UploadFileField
                accept="image/jpeg,image/jpg,image/png,.jpeg,.jpg,.png,.pdf,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                title="Passport"
                icon={<UploadIcon />}
                onChange={(file) => handleFileChange(file, "passport_image")}
                fileUrl={
                  typeof fileUploads.passport_image === "string"
                    ? fileUploads.passport_image
                    : undefined
                }
                showInstruction
              />
              {fieldErrors.passport_image && (
                <Box sx={{ color: "red", mt: 1, fontSize: "14px" }}>
                  {fieldErrors.passport_image}
                </Box>
              )}
            </Grid>
          </StyledGrid>
        )}

        {errorMessage && (
          <Box sx={{ color: "red", marginBottom: 2 }}>{errorMessage}</Box>
        )}

        <StepSubmitter
          label={"Next"}
          showPrevious={true}
          showNext={true}
          loading={loading}
        />
      </form>

      {/* Seal notice. Acknowledging it continues to the pending navigation the
          success path had already queued, so "proceed" resumes the normal flow
          rather than re-submitting. */}
      <ConfirmModal
        open={sealNoticeOpen}
        title="No official stamp found"
        message="No official stamp was found on the business documents you uploaded; if you are certain that an official stamp is present, you may proceed."
        confirmLabel="Proceed"
        cancelLabel="Re-upload"
        onConfirm={() => {
          setSealNoticeOpen(false);
          setSealAcknowledged(true);
          // Nothing was extracted or stored when the gate fired, so proceeding
          // means submitting again -- this time with consent attached.
          setResubmitAfterConsent(true);
        }}
        onCancel={() => {
          // Stay on the screen with the files intact so a different document can
          // be uploaded; that clears the cached answer and it is checked afresh.
          setSealNoticeOpen(false);
          setPendingNavigation(null);
        }}
      />

      {/* ✅ Success Popup Modal */}

      <AddressModal
        open={successPopupOpen}
        handleClose={handlePopupClose}
        formik={formik}
        closeModal={handlePopupClose}
        stateList={stateList}
        countryList={countryList}
        loading={addressLoading}
      />
    </Box>
  );
};
